/**
 * AI Dialog Actions Service
 * Handles AI-generated actions for opening chart dialogs, residence lists, and building dialogs
 * IMPORTANT: Supports Hospital, Community Transfer, Deceased, and Admission pages
 */

import { HOSPITAL_CARDS_TYPE } from '../types/hospital.type';
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from '../types/community-transfer.type';
import { DECEASED_CARDS_TYPE, DECEASED_CARDS_LABELS } from '../types/deceased.type';
import { ADMISSION_CARDS_TYPE } from '../types/admission.type';
import { PATIENT_RELATION, CHART_TAB_BUTTON } from '../types/chart-dialog.type';
import { PAGE_TYPE } from '../types/pages.type';
import logger from '../utilis/logger';

class AIDialogActionsService {
    constructor() {
        this.handleChartDialog = null;
        this.getStateData = null;
        this.initialized = false;
    }

    /**
     * Initialize the service with required handlers
     * @param {Function} handleChartDialog - Function to open chart dialogs
     * @param {Function} getStateData - Function to get current state data
     */
    initialize(handleChartDialog, getStateData) {
        this.handleChartDialog = handleChartDialog;
        this.getStateData = getStateData;
        this.initialized = true;
        
        logger.log('🎯 AI Dialog Actions Service initialized');
    }

    /**
     * Check if service is properly initialized
     * @returns {boolean} Initialization status
     */
    isInitialized() {
        return this.initialized && this.handleChartDialog && this.getStateData;
    }

    /**
     * Open chart dialog for a specific card type
     * @param {string} cardType - Type of card (e.g., 'hospitalizations', 'dcer', etc.)
     * @param {string} defaultTab - Default tab to open (optional)
     * @param {Object} options - Additional options
     * @returns {Object} Result of the action
     */
    async openChartDialog(cardType, defaultTab = 'Chart', options = {}) {
        try {
            if (!this.isInitialized()) {
                throw new Error('AI Dialog Actions Service not initialized');
            }

            // CRITICAL: Check if this is a custom card action or if we need to look it up
            let customCardMetadata = options.customCardMetadata;
            
            // If no custom card metadata provided, check if the cardType is a custom card accessor
            if (!customCardMetadata) {
                customCardMetadata = this.findCustomCardMetadata(cardType);
            }
            
            if (customCardMetadata) {
                // Handle custom card dialog opening
                const accessor = customCardMetadata.accessor || customCardMetadata.value;
                const cardTitle = customCardMetadata.cardTitle || customCardMetadata.label || customCardMetadata.title;
                
                // Get current state data to extract additional info if needed
                const stateData = this.getStateData ? this.getStateData() : {};
                const pageType = this.getCurrentPageType();
                const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
                const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
                const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
                const pageState = isCommunityPage ? stateData.communityTransfer : 
                                isDeceasedPage ? stateData.deceased : 
                                isAdmissionPage ? stateData.admission :
                                stateData.hospital;
                const filterListData = pageState?.filterListData || {};
                
                logger.log('🔍 [AI Dialog - Custom Card] Page State Detection:', {
                    pageType,
                    isCommunityPage,
                    isDeceasedPage,
                    isAdmissionPage,
                    hasPageState: !!pageState,
                    hasFilterListData: !!filterListData,
                    filterListDataKeys: Object.keys(filterListData).slice(0, 10),
                    accessor,
                    hasCardItems: (filterListData[accessor] || []).length
                });
                const cardItems = filterListData[accessor] || [];
                
                // CRITICAL: Build the question object for custom cards
                // This must match how DynamicCardItem calls handleChartDialog
                
                // Get the full card object
                const fullCard = customCardMetadata.fullCardData;
                
                // CRITICAL: customQuestionInputType must come from card.customQuestionInputType (top-level)
                const customQuestionInputType = fullCard?.customQuestionInputType || fullCard?.question?.customQuestionInputType;
                
                // Calculate isAnotherDashboard the EXACT same way DynamicCardItem does
                // DynamicCardItem line 45: const isAnotherDashboard = eleIsCustomTab && !dashboardType?.includes(pageType);
                const eleIsCustomTab = customCardMetadata.isCustomTab;
                const cardDashboardType = fullCard?.dashboardType || customCardMetadata.dashboardType || 'hospital';
                // CRITICAL: Use .includes() to match DynamicCardItem logic exactly
                const isAnotherDashboard = eleIsCustomTab && !cardDashboardType?.includes(pageType);
                
                logger.log('🔍 [AI Dialog - Custom Card] Dashboard Type Check:', {
                    cardDashboardType,
                    pageType,
                    includesCheck: cardDashboardType?.includes(pageType),
                    isAnotherDashboard,
                    eleIsCustomTab
                });
                
                // CRITICAL: Build question object EXACTLY as DynamicCardItem does (line 66-75)
                // isCustom is ALWAYS true for dynamic cards and custom tabs
                // isCustomTab indicates whether it's a filter-based custom tab (true) or question-based dynamic card (false)
                const question = {
                    isCustom: true, // ALWAYS true for custom/dynamic cards (matches DynamicCardItem line 67)
                    customQuestionInputType: customQuestionInputType,
                    isCustomTab: eleIsCustomTab || false,
                    isAnotherDashboard: isAnotherDashboard,
                    customTab: fullCard, // Full card object
                    level: cardItems[0]?.level || customCardMetadata.level,
                    cardTitle: cardTitle,
                    ...(eleIsCustomTab && { dashboardType: cardDashboardType })
                };
                
                logger.log('🔍 [AI Dialog - Custom Card] Question Object Built:', {
                    isCustom: question.isCustom,
                    isCustomTab: question.isCustomTab,
                    isAnotherDashboard: question.isAnotherDashboard,
                    customQuestionInputType: question.customQuestionInputType,
                    cardTitle: question.cardTitle,
                    dashboardType: question.dashboardType,
                    level: question.level
                });
                
                // Call the chart dialog handler with custom card parameters
                logger.log('🚀 [AI Dialog - Custom Card] Calling handleChartDialog with parameters:', {
                    param1_title: cardTitle,
                    param2_accessor: accessor,
                    param3_relation: null,
                    param4_isTotalCard: false,
                    param5_defaultTab: defaultTab,
                    param6_question: question
                });
                
                await this.handleChartDialog(
                    cardTitle,
                    accessor,
                    null,
                    false,
                    defaultTab,
                    question
                );
                
                logger.log('✅ [AI Dialog - Custom Card] Dialog opened successfully');
                
                return {
                    success: true,
                    message: `Opened ${cardTitle} ${this.getTabName(defaultTab)}`,
                    cardType: accessor,
                    dialogType: this.getDialogType(defaultTab),
                    actionPerformed: true,
                    isCustomCard: true
                };
            }

            // Handle standard cards
            const cardTypeMapping = this.getCardTypeMapping();
            const mappedCardType = cardTypeMapping[cardType] || cardType;
            
            if (!mappedCardType) {
                throw new Error(`Unknown card type: ${cardType}`);
            }

            // Get the appropriate title and relation for the card type
            const dialogConfig = this.getDialogConfig(mappedCardType);
            
            logger.log('🎯 Opening chart dialog:', {
                cardType,
                mappedCardType,
                defaultTab,
                dialogConfig
            });

            // Call the chart dialog handler with the correct parameters
            // handleChartDialog(title, type, relation, isTotalCard, defaultTab, question)
            await this.handleChartDialog(
                dialogConfig.title,
                mappedCardType,
                dialogConfig.relation,
                dialogConfig.isTotalCard,
                defaultTab,
                null // question parameter
            );

            return {
                success: true,
                message: `Opened ${dialogConfig.title} ${this.getTabName(defaultTab)}`,
                cardType: mappedCardType,
                dialogType: this.getDialogType(defaultTab),
                actionPerformed: true
            };

        } catch (error) {
            logger.error('❌ Error opening chart dialog:', error);
            return {
                success: false,
                error: `Failed to open chart dialog: ${error.message}`
            };
        }
    }

    /**
     * Find custom card metadata by card accessor or label
     * IMPORTANT: Now page-aware - searches in correct state based on current page
     * @param {string} cardIdentifier - Card accessor or label to search for
     * @returns {Object|null} Custom card metadata if found
     */
    findCustomCardMetadata(cardIdentifier) {
        if (!this.getStateData) {
            return null;
        }

        const stateData = this.getStateData();
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;

        // Get data from correct state based on page type
        const pageState = isCommunityPage ? stateData.communityTransfer : 
                        isDeceasedPage ? stateData.deceased : 
                        isAdmissionPage ? stateData.admission :
                        stateData.hospital;
        const filterListData = pageState?.filterListData || {};
        const dynamicCards = pageState?.dynamicCards || [];
        const patientList = pageState?.patientList || {};
        const customTabs = patientList.customTabs || [];
        
        const dashboardType = isCommunityPage ? 'communityTransfer' : 
                            isDeceasedPage ? 'deceased' : 
                            isAdmissionPage ? 'admission' :
                            'hospital';
        
        logger.log('🔍 [AI Dialog - findCustomCardMetadata] Page Detection:', {
            pageType,
            isAdmissionPage,
            dashboardType,
            hasPageState: !!pageState,
            hasDynamicCards: dynamicCards.length,
            hasCustomTabs: customTabs.length,
            hasFilterListData: Object.keys(filterListData).length,
            searchingFor: cardIdentifier
        });
        
        // STEP 1: Try to find in dynamicCards array (question-based custom cards)
        for (const card of dynamicCards) {
            const accessor = card.accessor;
            const label = (card.label || card.title || card.tableLabel || '').toLowerCase().trim();
            const cardId = cardIdentifier.toLowerCase().trim();
            
            // Match by accessor or label (case-insensitive)
            if (accessor === cardIdentifier || accessor?.toLowerCase() === cardId || label === cardId) {
                return {
                    accessor: accessor,
                    value: accessor,
                    label: card.label || card.title || card.tableLabel,
                    title: card.title || card.label || card.tableLabel,
                    cardTitle: card.title || card.label || card.tableLabel,
                    isCustom: true,
                    isCustomTab: false,
                    customQuestionInputType: card.customQuestionInputType || card.question?.customQuestionInputType,
                    dashboardType: dashboardType,
                    type: card.type,
                    customTab: card.type,
                    level: filterListData[accessor]?.[0]?.level,
                    fullCardData: card
                };
            }
        }
        
        // STEP 2: Try to find in customTabs array (filter-based custom cards)
        for (const tab of customTabs) {
            const accessor = tab.accessor;
            const label = (tab.label || tab.title || '').toLowerCase().trim();
            const cardId = cardIdentifier.toLowerCase().trim();
            
            // Match by accessor or label (case-insensitive)
            if (accessor === cardIdentifier || accessor?.toLowerCase() === cardId || label === cardId) {
                return {
                    accessor: accessor,
                    value: accessor,
                    label: tab.label || tab.title,
                    title: tab.title || tab.label,
                    cardTitle: tab.title || tab.label,
                    isCustom: false,
                    isCustomTab: true,
                    customQuestionInputType: null,
                    dashboardType: tab.dashboardType || tab.page || dashboardType,
                    type: tab.type,
                    customTab: tab,
                    level: filterListData[accessor]?.[0]?.level,
                    fullCardData: tab
                };
            }
        }
        
        // STEP 3: Fallback - Check if this accessor exists in filterListData (legacy approach)
        if (filterListData[cardIdentifier]) {
            const cardItems = filterListData[cardIdentifier];
            const firstItem = cardItems[0];
            
            // Check if this is a custom card by looking for custom card indicators
            if (firstItem && (firstItem.isCustomCard || firstItem.questionId || firstItem.isCustomTab)) {
                logger.log('⚠️ Found custom card via fallback (filterListData):', firstItem.label);
                return {
                    accessor: cardIdentifier,
                    value: cardIdentifier,
                    label: firstItem.label || firstItem.name || firstItem.title || cardIdentifier,
                    title: firstItem.title || firstItem.label || firstItem.name,
                    cardTitle: firstItem.label || firstItem.name || firstItem.title,
                    isCustom: firstItem.isCustomCard || !!firstItem.questionId,
                    isCustomTab: firstItem.isCustomTab || false,
                    customQuestionInputType: firstItem.customQuestionInputType,
                    dashboardType: firstItem.dashboardType || dashboardType,
                    type: firstItem.type,
                    customTab: firstItem.customTab,
                    level: firstItem.level
                };
            }
        }
        
        // STEP 4: Try to find by label/title match in filterListData (legacy fallback)
        const lowerCardId = cardIdentifier.toLowerCase().trim();
        for (const [accessor, items] of Object.entries(filterListData)) {
            if (Array.isArray(items) && items.length > 0) {
                const firstItem = items[0];
                const label = (firstItem.label || firstItem.name || firstItem.title || '').toLowerCase().trim();
                
                if (label === lowerCardId && (firstItem.isCustomCard || firstItem.questionId || firstItem.isCustomTab)) {
                    logger.log('⚠️ Found custom card via fallback label match:', firstItem.label);
                    return {
                        accessor: accessor,
                        value: accessor,
                        label: firstItem.label || firstItem.name || firstItem.title,
                        title: firstItem.title || firstItem.label || firstItem.name,
                        cardTitle: firstItem.label || firstItem.name || firstItem.title,
                        isCustom: firstItem.isCustomCard || !!firstItem.questionId,
                        isCustomTab: firstItem.isCustomTab || false,
                        customQuestionInputType: firstItem.customQuestionInputType,
                        dashboardType: firstItem.dashboardType || 'hospital',
                        type: firstItem.type,
                        customTab: firstItem.customTab,
                        level: firstItem.level
                    };
                }
            }
        }
        
        return null;
    }

    /**
     * Open residence list dialog for a specific card type
     * @param {string} cardType - Type of card
     * @param {Object} options - Additional options
     * @returns {Object} Result of the action
     */
    async openResidenceListDialog(cardType, options = {}) {
        try {
            if (!this.isInitialized()) {
                throw new Error('AI Dialog Actions Service not initialized');
            }

            const cardTypeMapping = this.getCardTypeMapping();
            const mappedCardType = cardTypeMapping[cardType] || cardType;
            
            // Open chart dialog with residence/patient details tab (defaultTab = null for residence list)
            const result = await this.openChartDialog(mappedCardType, null, options);
            
            if (result.success) {
                result.message = result.message.replace('chart view', 'patient list');
                result.dialogType = 'residence';
            }

            return result;

        } catch (error) {
            logger.error('❌ Error opening residence list dialog:', error);
            return {
                success: false,
                error: `Failed to open residence list: ${error.message}`
            };
        }
    }

    /**
     * Open building/facility dialog
     * @param {Object} options - Additional options including cardType for specific card building view
     * @returns {Object} Result of the action
     */
    async openBuildingDialog(options = {}) {
        try {
            if (!this.isInitialized()) {
                throw new Error('AI Dialog Actions Service not initialized');
            }

            // Determine the card type for building view
            let targetCardType = HOSPITAL_CARDS_TYPE.HOSPITAL_DATA; // Default to hospital data
            let cardDisplayName = 'facility';
            
            // If a specific card type is requested, map it to the appropriate card type
            if (options.cardType) {
                const cardTypeMapping = this.getCardTypeMapping();
                const mappedCardType = cardTypeMapping[options.cardType] || options.cardType;
                
                // Map backend card types to frontend card types for building view
                const buildingCardTypeMapping = {
                    'ninetyDays': HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
                    'hospitalizations': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
                    'dcer': HOSPITAL_CARDS_TYPE.DCER_DATA,
                    'insurance': HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
                    'returns': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
                    'floors': HOSPITAL_CARDS_TYPE.FLOORS_DATA,
                    'doctor': HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
                    'days': HOSPITAL_CARDS_TYPE.DAYS_DATA,
                    'dx': HOSPITAL_CARDS_TYPE.DX_DATA,
                    'shift': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
                    'nurse': HOSPITAL_CARDS_TYPE.NURSE_DATA,
                    'hospital': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA
                };
                
                targetCardType = buildingCardTypeMapping[options.cardType] || mappedCardType || HOSPITAL_CARDS_TYPE.HOSPITAL_DATA;
                cardDisplayName = options.cardType === 'ninetyDays' ? '90 Days Analysis' : 
                                 options.cardType === 'dcer' ? 'DC/ER' :
                                 options.cardType === 'hospitalizations' ? 'Hospitalizations' :
                                 options.cardType || 'facility';
                
                logger.log('🏢 Opening card-specific building dialog:', {
                    requestedCardType: options.cardType,
                    mappedCardType,
                    targetCardType,
                    cardDisplayName
                });
            }

            // Open chart dialog with building tab for the specific card type
            const result = await this.openChartDialog(targetCardType, CHART_TAB_BUTTON.BUILDING, options);
            
            if (result.success) {
                result.message = result.message.replace('chart view', `${cardDisplayName} facility breakdown`);
                result.dialogType = 'building';
                result.specificCard = options.cardType || null;
            }

            return result;

        } catch (error) {
            logger.error('❌ Error opening building dialog:', error);
            return {
                success: false,
                error: `Failed to open building dialog: ${error.message}`
            };
        }
    }

    /**
     * Get tab name for display
     * @param {string} defaultTab - Default tab value
     * @returns {string} Tab display name
     */
    getTabName(defaultTab) {
        switch (defaultTab) {
            case 'Chart':
            case CHART_TAB_BUTTON.CHART:
                return 'chart view';
            case 'Building':
            case CHART_TAB_BUTTON.BUILDING:
                return 'building view';
            case null:
                return 'patient list';
            default:
                return 'dialog';
        }
    }

    /**
     * Get dialog type from tab
     * @param {string} defaultTab - Default tab value
     * @returns {string} Dialog type
     */
    getDialogType(defaultTab) {
        switch (defaultTab) {
            case 'Chart':
            case CHART_TAB_BUTTON.CHART:
                return 'chart';
            case 'Building':
            case CHART_TAB_BUTTON.BUILDING:
                return 'building';
            case null:
                return 'residence';
            default:
                return 'dialog';
        }
    }

    /**
     * Detect current page type from state
     * @returns {string} Page type
     */
    getCurrentPageType() {
        if (!this.getStateData) {
            return PAGE_TYPE.HOSPITAL;
        }

        const stateData = this.getStateData();
        
        // Check for community transfer specific state
        if (stateData.communityTransfer && Object.keys(stateData.communityTransfer).length > 0) {
            return PAGE_TYPE.COMMUNITY_TRANSFER;
        }
        
        // Check for deceased specific state
        if (stateData.deceased && Object.keys(stateData.deceased).length > 0) {
            return PAGE_TYPE.DECEASED;
        }
        
        // Check for admission specific state
        if (stateData.admission && Object.keys(stateData.admission).length > 0) {
            return PAGE_TYPE.ADMISSION;
        }
        
        // Default to hospital
        return PAGE_TYPE.HOSPITAL;
    }

    /**
     * Get card type mapping from user-friendly names to system constants
     * IMPORTANT: Returns page-specific mappings based on current page
     * @returns {Object} Card type mapping
     */
    getCardTypeMapping() {
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;

        if (isAdmissionPage) {
            // ========== ADMISSION PAGE MAPPINGS ==========
            return {
                // Admission page card types
                'doctor': ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                'doctors': ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                'physician': ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                'physicians': ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                
                'days': ADMISSION_CARDS_TYPE.DAYS_DATA,
                'day': ADMISSION_CARDS_TYPE.DAYS_DATA,
                'weekday': ADMISSION_CARDS_TYPE.DAYS_DATA,
                'day of week': ADMISSION_CARDS_TYPE.DAYS_DATA,
                
                'dx': ADMISSION_CARDS_TYPE.DX_DATA,
                'diagnosis': ADMISSION_CARDS_TYPE.DX_DATA,
                'diagnoses': ADMISSION_CARDS_TYPE.DX_DATA,
                'diagnostic': ADMISSION_CARDS_TYPE.DX_DATA,
                
                'insurance': ADMISSION_CARDS_TYPE.INSURANCE_DATA,
                'payer': ADMISSION_CARDS_TYPE.INSURANCE_DATA,
                'medicare': ADMISSION_CARDS_TYPE.INSURANCE_DATA,
                'medicaid': ADMISSION_CARDS_TYPE.INSURANCE_DATA,
                
                'floors': ADMISSION_CARDS_TYPE.FLOORS_DATA,
                'floor': ADMISSION_CARDS_TYPE.FLOORS_DATA,
                'unit': ADMISSION_CARDS_TYPE.FLOORS_DATA,
                'units': ADMISSION_CARDS_TYPE.FLOORS_DATA,
                
                'hospital': ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                'hospitals': ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                'source hospital': ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                'admission source': ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                
                'total': ADMISSION_CARDS_TYPE.TOTAL,
                'admission': ADMISSION_CARDS_TYPE.ADMISSION,
                'admissions': ADMISSION_CARDS_TYPE.ADMISSION,
                'readmission': ADMISSION_CARDS_TYPE.READMISSION,
                'readmissions': ADMISSION_CARDS_TYPE.READMISSION
            };
        }

        if (isCommunityPage) {
            // ========== COMMUNITY TRANSFER PAGE MAPPINGS ==========
            return {
                // Community transfer types
                'safeDischarge': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
                'safe discharge': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
                'safely discharged': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
                'community discharge': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
                
                'SNF': CO_TRANSFER_CARDS_TYPE.SNF,
                'snf': CO_TRANSFER_CARDS_TYPE.SNF,
                'skilled nursing': CO_TRANSFER_CARDS_TYPE.SNF,
                'nursing facility': CO_TRANSFER_CARDS_TYPE.SNF,
                
                'AMA': CO_TRANSFER_CARDS_TYPE.AMA,
                'ama': CO_TRANSFER_CARDS_TYPE.AMA,
                'against medical advice': CO_TRANSFER_CARDS_TYPE.AMA,
                
                // Shared card types
                'insurance': CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA,
                'returns': CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
                'returned': CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
                'ninety days': CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
                '90 days': CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
                'floors': CO_TRANSFER_CARDS_TYPE.FLOORS_DATA,
                'doctors': CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
                'doctor': CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
                'days': CO_TRANSFER_CARDS_TYPE.DAYS_DATA,
                
                // Community-specific cards
                'safeDischargeAssLiv': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
                'assisted living': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
                'snfFacility': CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
                'snf facility': CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
                
                'total': CO_TRANSFER_CARDS_TYPE.TOTAL
            };
        }

        if (isDeceasedPage) {
            // ========== DECEASED PAGE MAPPINGS ==========
            return {
                // Deceased-specific types
                'deceased': DECEASED_CARDS_TYPE.TOTAL,
                'total': DECEASED_CARDS_TYPE.TOTAL,
                'mortality': DECEASED_CARDS_TYPE.TOTAL,
                'death': DECEASED_CARDS_TYPE.TOTAL,
                'died': DECEASED_CARDS_TYPE.TOTAL,
                'passed away': DECEASED_CARDS_TYPE.TOTAL,
                
                // Shared card types with deceased page
                'insurance': DECEASED_CARDS_TYPE.INSURANCE_DATA,
                'payer': DECEASED_CARDS_TYPE.INSURANCE_DATA,
                'medicare': DECEASED_CARDS_TYPE.INSURANCE_DATA,
                'medicaid': DECEASED_CARDS_TYPE.INSURANCE_DATA,
                
                'floors': DECEASED_CARDS_TYPE.FLOORS_DATA,
                'floor': DECEASED_CARDS_TYPE.FLOORS_DATA,
                'units': DECEASED_CARDS_TYPE.FLOORS_DATA,
                'unit': DECEASED_CARDS_TYPE.FLOORS_DATA,
                
                'doctors': DECEASED_CARDS_TYPE.DOCTOR_DATA,
                'doctor': DECEASED_CARDS_TYPE.DOCTOR_DATA,
                'physician': DECEASED_CARDS_TYPE.DOCTOR_DATA,
                'physicians': DECEASED_CARDS_TYPE.DOCTOR_DATA,
                
                'days': DECEASED_CARDS_TYPE.DAYS_DATA,
                'day': DECEASED_CARDS_TYPE.DAYS_DATA,
                'weekday': DECEASED_CARDS_TYPE.DAYS_DATA,
                'weekend': DECEASED_CARDS_TYPE.DAYS_DATA,
                
                '90 days': DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                'ninety days': DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                '90-day': DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                'ninety-day': DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                'ninetyDays': DECEASED_CARDS_TYPE.NINETY_DAYS_DATA
            };
        }

        // ========== HOSPITAL PAGE MAPPINGS ==========
        return {
            // User-friendly names to system constants
            'hospitalizations': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            'hospitalization': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            'new hospitalizations': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            're-hospitalizations': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            'rehospitalizations': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            
            'dc': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'er': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'dcer': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'dc/er': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'discharge': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'emergency': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'emergency room': HOSPITAL_CARDS_TYPE.DCER_DATA,
            
            'insurance': HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
            'payer': HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
            'medicare': HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
            'medicaid': HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
            
            'returns': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'returned': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'return': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'didn\'t return': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'did not return': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'return and did not return': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'returned / didn\'t return': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            
            '90 days': HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
            'ninety days': HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
            '90-day': HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
            'ninety-day': HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
            
            'floors': HOSPITAL_CARDS_TYPE.FLOORS_DATA,
            'floor': HOSPITAL_CARDS_TYPE.FLOORS_DATA,
            'units': HOSPITAL_CARDS_TYPE.FLOORS_DATA,
            'unit': HOSPITAL_CARDS_TYPE.FLOORS_DATA,
            
            'doctors': HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
            'doctor': HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
            'physician': HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
            'physicians': HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
            
            'days': HOSPITAL_CARDS_TYPE.DAYS_DATA,
            'day': HOSPITAL_CARDS_TYPE.DAYS_DATA,
            'weekday': HOSPITAL_CARDS_TYPE.DAYS_DATA,
            'weekend': HOSPITAL_CARDS_TYPE.DAYS_DATA,
            
            'diagnosis': HOSPITAL_CARDS_TYPE.DX_DATA,
            'diagnoses': HOSPITAL_CARDS_TYPE.DX_DATA,
            'dx': HOSPITAL_CARDS_TYPE.DX_DATA,
            
            'shifts': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            'shift': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            'morning': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            'evening': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            'night': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            
            'nurses': HOSPITAL_CARDS_TYPE.NURSE_DATA,
            'nurse': HOSPITAL_CARDS_TYPE.NURSE_DATA,
            'per nurse': HOSPITAL_CARDS_TYPE.NURSE_DATA,
            
            'hospitals': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'hospital': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'facilities': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'facility': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'building': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'buildings': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'hospitals residents transferred to': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'transferred to': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            
            'total': HOSPITAL_CARDS_TYPE.TOTAL,
            'planned': HOSPITAL_CARDS_TYPE.PLANNED,
            'unplanned': HOSPITAL_CARDS_TYPE.UNPLANNED,
            'planned transfers': HOSPITAL_CARDS_TYPE.PLANNED,
            'unplanned transfers': HOSPITAL_CARDS_TYPE.UNPLANNED,
            'planned hospital transfer': HOSPITAL_CARDS_TYPE.PLANNED,
            'unplanned hospital transfer': HOSPITAL_CARDS_TYPE.UNPLANNED
        };
    }

    /**
     * Get dialog configuration for a card type
     * IMPORTANT: Returns page-specific configurations based on current page
     * @param {string} cardType - Card type constant
     * @returns {Object} Dialog configuration
     */
    getDialogConfig(cardType) {
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;

        if (isAdmissionPage) {
            // ========== ADMISSION PAGE CONFIGS ==========
            const admissionConfigs = {
                [ADMISSION_CARDS_TYPE.DOCTOR_DATA]: {
                    title: 'Per Doctor',
                    relation: PATIENT_RELATION.DOCTOR,
                    isTotalCard: false
                },
                [ADMISSION_CARDS_TYPE.DAYS_DATA]: {
                    title: 'Per Day of the Week',
                    relation: null,
                    isTotalCard: false
                },
                [ADMISSION_CARDS_TYPE.DX_DATA]: {
                    title: 'Per Diagnosis',
                    relation: PATIENT_RELATION.DX,
                    isTotalCard: false
                },
                [ADMISSION_CARDS_TYPE.INSURANCE_DATA]: {
                    title: 'Per Insurance',
                    relation: PATIENT_RELATION.PAYER_INSURANCE,
                    isTotalCard: false
                },
                [ADMISSION_CARDS_TYPE.FLOORS_DATA]: {
                    title: 'Per Floor',
                    relation: PATIENT_RELATION.UNIT,
                    isTotalCard: false
                },
                [ADMISSION_CARDS_TYPE.HOSPITAL_DATA]: {
                    title: 'Per Hospital',
                    relation: PATIENT_RELATION.HOSPITAL,
                    isTotalCard: false
                },
                [ADMISSION_CARDS_TYPE.TOTAL]: {
                    title: 'Total Admissions',
                    relation: null,
                    isTotalCard: true
                },
                [ADMISSION_CARDS_TYPE.ADMISSION]: {
                    title: 'New Admissions',
                    relation: null,
                    isTotalCard: true
                },
                [ADMISSION_CARDS_TYPE.READMISSION]: {
                    title: 'Readmissions',
                    relation: null,
                    isTotalCard: true
                }
            };

            return admissionConfigs[cardType] || {
                title: 'Chart View',
                relation: null,
                isTotalCard: false
            };
        }

        if (isCommunityPage) {
            // ========== COMMUNITY TRANSFER PAGE CONFIGS ==========
            const communityConfigs = {
                [CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE]: {
                    title: COMMUNITY_CARD_LABELS.safeDischarge,
                    relation: null,
                    isTotalCard: true
                },
                [CO_TRANSFER_CARDS_TYPE.SNF]: {
                    title: COMMUNITY_CARD_LABELS.SNF,
                    relation: null,
                    isTotalCard: true
                },
                [CO_TRANSFER_CARDS_TYPE.AMA]: {
                    title: COMMUNITY_CARD_LABELS.AMA,
                    relation: null,
                    isTotalCard: true
                },
                [CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA]: {
                    title: COMMUNITY_CARD_LABELS.insuranceData,
                    relation: PATIENT_RELATION.INSURANCE,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.RETURNS_DATA]: {
                    title: COMMUNITY_CARD_LABELS.returnsData,
                    relation: null,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA]: {
                    title: COMMUNITY_CARD_LABELS.sixtyDaysData,
                    relation: null,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.FLOORS_DATA]: {
                    title: COMMUNITY_CARD_LABELS.floorsData,
                    relation: PATIENT_RELATION.UNIT,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA]: {
                    title: COMMUNITY_CARD_LABELS.doctorData,
                    relation: PATIENT_RELATION.DOCTOR,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.DAYS_DATA]: {
                    title: COMMUNITY_CARD_LABELS.daysData,
                    relation: null,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA]: {
                    title: COMMUNITY_CARD_LABELS.safeDischargeAssLivData,
                    relation: PATIENT_RELATION.TRANSFER_TO_WHICH_ASSISTED_LIVING,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA]: {
                    title: COMMUNITY_CARD_LABELS.snfFacilityData,
                    relation: PATIENT_RELATION.SNF,
                    isTotalCard: false
                },
                [CO_TRANSFER_CARDS_TYPE.TOTAL]: {
                    title: COMMUNITY_CARD_LABELS.totalMain,
                    relation: null,
                    isTotalCard: true
                }
            };

            return communityConfigs[cardType] || {
                title: 'Chart View',
                relation: null,
                isTotalCard: false
            };
        }

        if (isDeceasedPage) {
            // ========== DECEASED PAGE CONFIGS ==========
            const deceasedConfigs = {
                [DECEASED_CARDS_TYPE.TOTAL]: {
                    title: DECEASED_CARDS_LABELS.total,
                    relation: null,
                    isTotalCard: true
                },
                [DECEASED_CARDS_TYPE.INSURANCE_DATA]: {
                    title: DECEASED_CARDS_LABELS.insuranceData,
                    relation: PATIENT_RELATION.INSURANCE,
                    isTotalCard: false
                },
                [DECEASED_CARDS_TYPE.DOCTOR_DATA]: {
                    title: DECEASED_CARDS_LABELS.doctorData,
                    relation: PATIENT_RELATION.DOCTOR,
                    isTotalCard: false
                },
                [DECEASED_CARDS_TYPE.FLOORS_DATA]: {
                    title: DECEASED_CARDS_LABELS.floorsData,
                    relation: PATIENT_RELATION.UNIT,
                    isTotalCard: false
                },
                [DECEASED_CARDS_TYPE.DAYS_DATA]: {
                    title: DECEASED_CARDS_LABELS.daysData,
                    relation: null,
                    isTotalCard: false
                },
                [DECEASED_CARDS_TYPE.NINETY_DAYS_DATA]: {
                    title: DECEASED_CARDS_LABELS.ninetyDaysData,
                    relation: null,
                    isTotalCard: false
                }
            };

            return deceasedConfigs[cardType] || {
                title: 'Chart View',
                relation: null,
                isTotalCard: false
            };
        }

        // ========== HOSPITAL PAGE CONFIGS ==========
        const configs = {
            [HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS]: {
                title: 'New Hospitalizations & Re-Hospitalizations',
                relation: null,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.DCER_DATA]: {
                title: 'DC / ER',
                relation: null,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.INSURANCE_DATA]: {
                title: 'Transfers per Insurance',
                relation: PATIENT_RELATION.INSURANCE,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.RETURNS_DATA]: {
                title: 'Returned / Didn\'t Return',
                relation: null,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA]: {
                title: '90 Days Analysis',
                relation: null,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.FLOORS_DATA]: {
                title: 'Per Floor',
                relation: PATIENT_RELATION.UNIT,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.DOCTOR_DATA]: {
                title: 'Per Doctor',
                relation: PATIENT_RELATION.DOCTOR,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.DAYS_DATA]: {
                title: 'Per Day of the Week',
                relation: null,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.DX_DATA]: {
                title: 'Per Diagnosis',
                relation: PATIENT_RELATION.DX,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.SHIFT_DATA]: {
                title: 'Per Shift',
                relation: null,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.NURSE_DATA]: {
                title: 'Per Nurse',
                relation: PATIENT_RELATION.NURSE,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.HOSPITAL_DATA]: {
                title: 'Hospitals residents transferred to',
                relation: PATIENT_RELATION.HOSPITAL,
                isTotalCard: false
            },
            [HOSPITAL_CARDS_TYPE.TOTAL]: {
                title: 'Total Hospital Transfers',
                relation: null,
                isTotalCard: true
            },
            [HOSPITAL_CARDS_TYPE.PLANNED]: {
                title: 'Planned Transfers',
                relation: null,
                isTotalCard: true
            },
            [HOSPITAL_CARDS_TYPE.UNPLANNED]: {
                title: 'Unplanned Transfers',
                relation: null,
                isTotalCard: true
            }
        };

        return configs[cardType] || {
            title: 'Chart View',
            relation: null,
            isTotalCard: false
        };
    }

    /**
     * Get available dialog actions based on current state
     * @returns {Array} Available dialog actions
     */
    getAvailableDialogActions() {
        if (!this.isInitialized()) {
            return [];
        }

        try {
            const actions = [];

            // Add chart dialog actions for each available card type
            const cardTypes = [
                { key: HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS, name: 'Hospitalizations' },
                { key: HOSPITAL_CARDS_TYPE.DCER_DATA, name: 'DC/ER' },
                { key: HOSPITAL_CARDS_TYPE.INSURANCE_DATA, name: 'Insurance' },
                { key: HOSPITAL_CARDS_TYPE.RETURNS_DATA, name: 'Returns' },
                { key: HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA, name: '90 Days Analysis' },
                { key: HOSPITAL_CARDS_TYPE.FLOORS_DATA, name: 'Floors' },
                { key: HOSPITAL_CARDS_TYPE.DOCTOR_DATA, name: 'Doctors' },
                { key: HOSPITAL_CARDS_TYPE.DAYS_DATA, name: 'Days' },
                { key: HOSPITAL_CARDS_TYPE.DX_DATA, name: 'Diagnosis' },
                { key: HOSPITAL_CARDS_TYPE.SHIFT_DATA, name: 'Shifts' },
                { key: HOSPITAL_CARDS_TYPE.NURSE_DATA, name: 'Nurses' },
                { key: HOSPITAL_CARDS_TYPE.HOSPITAL_DATA, name: 'Hospitals' },
                { key: HOSPITAL_CARDS_TYPE.TOTAL, name: 'Total' },
                { key: HOSPITAL_CARDS_TYPE.PLANNED, name: 'Planned' },
                { key: HOSPITAL_CARDS_TYPE.UNPLANNED, name: 'Unplanned' }
            ];

            cardTypes.forEach(cardType => {
                // Chart view action
                actions.push({
                    type: 'open_chart_dialog',
                    cardType: cardType.key,
                    description: `Open ${cardType.name} chart view`,
                    dialogType: 'chart'
                });

                // Residence list action (for cards that have patient details)
                if (this.hasPatientDetails(cardType.key)) {
                    actions.push({
                        type: 'open_residence_dialog',
                        cardType: cardType.key,
                        description: `Open ${cardType.name} residence list`,
                        dialogType: 'residence'
                    });
                }
            });

            // Building/facility dialog action
            actions.push({
                type: 'open_building_dialog',
                description: 'Open facility/building view',
                dialogType: 'building'
            });

            return actions;

        } catch (error) {
            logger.error('Error getting available dialog actions:', error);
            return [];
        }
    }

    /**
     * Check if a card type has patient details available
     * @param {string} cardType - Card type to check
     * @returns {boolean} Whether patient details are available
     */
    hasPatientDetails(cardType) {
        const cardTypesWithPatientDetails = [
            HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            HOSPITAL_CARDS_TYPE.DCER_DATA,
            HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
            HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            HOSPITAL_CARDS_TYPE.FLOORS_DATA,
            HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
            HOSPITAL_CARDS_TYPE.DAYS_DATA,
            HOSPITAL_CARDS_TYPE.DX_DATA,
            HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            HOSPITAL_CARDS_TYPE.NURSE_DATA,
            HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            HOSPITAL_CARDS_TYPE.TOTAL,
            HOSPITAL_CARDS_TYPE.PLANNED,
            HOSPITAL_CARDS_TYPE.UNPLANNED
        ];

        return cardTypesWithPatientDetails.includes(cardType);
    }

    /**
     * Get current dialog status
     * @returns {Object} Current status
     */
    getCurrentDialogStatus() {
        return {
            initialized: this.isInitialized(),
            availableActions: this.getAvailableDialogActions().length,
            canOpenChartDialogs: this.isInitialized(),
            canOpenResidenceDialogs: this.isInitialized(),
            canOpenBuildingDialogs: this.isInitialized()
        };
    }
}

// Create singleton instance
const aiDialogActionsService = new AIDialogActionsService();

export default aiDialogActionsService;
